<template>
  <div class="card">
    <header class="card-header">
      <span class="card-header-title">
        {{ headerText }}
        &nbsp;&nbsp;
          <div v-if="true">
            <GoalStatusIconGroup :goal-id="activeGoal.id" />
          </div>
      </span>
    </header>
    <div v-if="!isLoading" class="card-content">
      <GoalTextInput
        :active-goal-id="activeGoal.id"
        :active-goal-date="activeGoal.goalDate || this.goalDate"
        :active-user-id="activeUserId"
      />
      <b-collapse
        v-if="activeGoal.goalStatusExplanation && activeWorkspace.promptForGoalExplanationPreference"
        :key="activeGoal.id"
        :open="isExplanationExpandedByDefault"
        position="is-bottom"
      >
        <a
          class="has-text-grey is-size-6"
          slot="trigger"
          slot-scope="props"
          aria-controls="explanation-content"
        >
          <b-icon size="is-small" :icon="!props.open ? 'menu-down' : 'menu-up'"></b-icon>
          {{ !props.open ? 'More' : 'Less' }}
        </a>
        <p>
          {{ activeGoal.goalStatusExplanation }} |

          <a
            class="has-text-primary"
            style="text-decoration: underline;"
            @click="isGoalDetailModalOpen = true"
            @keyup.enter="isGoalDetailModalOpen = true"
          >
            Edit
          </a>
        </p>
      </b-collapse>

      <goal-status-detail-modal
        :is-modal-open="isGoalDetailModalOpen"
        :goal-status="activeGoal.goalStatus"
        :goal-status-detail-text="activeGoal.goalStatusExplanation"
        @modal-close="handleUpdateGoalStatus"
      />
    </div>
    <div v-else style="position: relative;" class="u-p-b-xlarge">
      <b-loading v-if="isLoading" :is-full-page="false" :active.sync="isLoading"></b-loading>
    </div>
  </div>
</template>

<script>
import { mapState, mapActions, mapGetters } from 'vuex';
import * as GoalActions from '@/store/actions/Goal.actions';
import {
  isToday,
  isYesterday,
  isTomorrow,
  parseDate,
  getWeekNumber,
  shortDateOptions,
} from '@/helpers/dateHelpers';
import GoalStatusIconGroup from '@/components/GoalStatusIconGroup.vue';
import GoalTextInput from '@/components/GoalTextInput.vue';
import GoalStatusDetailModal from '@/components/GoalStatusDetailModal.vue';

export default {
  name: 'DailyGoal',

  components: {
    GoalStatusIconGroup,
    GoalTextInput,
    GoalStatusDetailModal,
  },

  props: {
    isLoading: {
      type: Boolean,
      required: false,
      default: false,
    },
    activeUserId: {
      type: String,
      required: true,
    },
    goalDate: {
      type: String,
      required: true,
    },
    // During standup the presenter shouldn't have to click 'More' to read a missed or
    // changed goal's detail, so the explanation starts expanded there.
    isExplanationExpandedByDefault: {
      type: Boolean,
      required: false,
      default: false,
    },
  },

  data() {
    return {
      isEditingGoal: false,
      isGoalDetailModalOpen: false,
    };
  },

  computed: {
    ...mapState({
      goals: state => state.goal.goals,
    }),
    ...mapGetters([
      'activeWorkspace',
    ]),
    activeGoal() {
      const [foundGoal] = Object.values(this.goals)
        .filter(goal => goal.userId === this.activeUserId && goal.goalDate === this.goalDate);

      return foundGoal || {};
    },
    isActiveDateSameWeekAsToday() {
      if (!this.goalDate) return false;

      return getWeekNumber(new Date(parseDate(this.goalDate)))[1] === getWeekNumber(new Date())[1];
    },
    headerText() {
      let headerText = '';
      if (isToday(this.goalDate)) {
        headerText = 'Today\'s Goal';
      } else if (isYesterday(this.goalDate)) {
        headerText = 'Yesterday\'s Goal';
      } else if (isTomorrow(this.goalDate)) {
        headerText = 'Tomorrow\'s Goal';
      } else {
        const day = new Date(parseDate(this.goalDate)).toLocaleString('en-US', shortDateOptions);
        headerText = `${day}'s Goal`;
      }

      return headerText;
    },
  },

  methods: {
    ...mapActions({
      updateGoal: GoalActions.UPDATE_GOAL,
    }),
    handleUpdateGoalStatus(goalStatusPayload) {
      const { goalStatusExplanation } = goalStatusPayload;
      this.isGoalDetailModalOpen = false;

      this.updateGoal({ goalId: this.activeGoal.id, updatedGoal: { goalStatusExplanation } });
    },
  },
};
</script>

<style lang="scss" scoped>
</style>
